import React, { useState, useEffect, useCallback } from 'react';
import { awsReservationsService, awsInstancesService } from '../services/api';
import { fmtDate, getTodayStr } from '../utils/formatters';
import { SortHeader, useSortState } from './shared/SortableTableHeader';
import CascaDeModal from './shared/CascaDeModal';
import { useConfirm } from './shared/ConfirmDialog';
import { useAviso } from './shared/Aviso';
import { campo, campoSomenteLeitura, useEstadoDaCarga, EstadoDaLista, A_PROPRIA_LISTA_DIZ_O_VAZIO, textos, mensagemDoErroDeCarga } from '../design';
import { TONE_TEXT, TONE_BADGE, TONE_BORDER } from '../utils/statusColors';
import { CELULA, CascaDaLista, CabecalhoDaLista, ColunaDaLista, LinhaDaLista, CelulaAcoes, CartaoDoTopo, barraDoEstado } from './shared/lista/PecasDaLista';
import {
  confirmacaoDeAssociar, confirmacaoDeRemoverCliente, confirmacaoDeExcluirReserva, FRASE_DA_RESERVA_DA_AWS,
  confirmacaoDeSilenciar, confirmacaoDeSobDemanda, confirmacaoDeDesvincular,
} from '../utils/infra';
import { nomeDeExibicao } from '../utils/nomeDeExibicao';

const Icon = ({ name, className = 'w-5 h-5' }: { name: string; className?: string }) => {
  const icons: any = {
    server: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01' /></svg>,
    cloud: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z' /></svg>,
    x: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M6 18L18 6M6 6l12 12' /></svg>,
    plus: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 4v16m8-8H4' /></svg>,
    trash: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16' /></svg>,
    search: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z' /></svg>,
    edit: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z' /></svg>,
    link: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1' /></svg>,
    unlink: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636' /></svg>,
    refresh: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15' /></svg>,
    alertTriangle: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z' /></svg>,
    calendar: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z' /></svg>,
    check: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M5 13l4 4L19 7' /></svg>,
    dollar: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z' /></svg>,
    chart: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z' /></svg>,
    user: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z' /></svg>,
    bell: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9' /></svg>,
    bellOff: <svg className={className} fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M13.73 21a2 2 0 01-3.46 0M18.63 13A17.89 17.89 0 0118 8M6.26 6.26A5.86 5.86 0 006 8c0 7-3 9-3 9h14M18 8a6 6 0 00-9.33-5M1 1l22 22' /></svg>,
  };
  return icons[name] || null;
};


const fmt = (v: any) => {
  const n = parseFloat(String(v));
  return isNaN(n) ? '0,00' : n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

type ReservationFilter = 'all' | 'active' | 'expired' | 'cancelled' | 'idle' | 'expiring';

// Vagas de reserva sem instancia ocupando. Vaga so e consumida por instancia
// RUNNING: o auto-link do backend desvincula parada e "sob demanda", entao
// parar a maquina ja devolve a vaga e a reserva segue sendo cobrada.
const idleUnitsOf = (r: AwsReservation): number =>
  Math.max(0, (r.instanceCount || 0) - (r.linkedInstances?.length || 0));

// monthlyRecurring e o custo da LINHA inteira, por isso divide por instanceCount
// antes de multiplicar pelas vagas ociosas (linha pode estar meio usada: 19/20).
const idleCostOf = (r: AwsReservation): number => {
  const count = r.instanceCount || 0;
  if (count <= 0) return 0;
  return (parseFloat(String(r.monthlyRecurring)) || 0) / count * idleUnitsOf(r);
};

interface AwsReservation {
  id: number;
  reservationId: string;
  instanceType: string;
  platform: string;
  region: string;
  availabilityZone?: string;
  scope: string;
  offeringClass: string;
  offeringType: string;
  term: number;
  instanceCount: number;
  startDate: string;
  endDate: string;
  status: string;
  fixedPrice: number;
  usagePrice: number;
  monthlyRecurring: number;
  description?: string;
  linkedInstances?: string[];
  alertDismissed?: boolean;
  lastSyncAt?: string;
}

interface InstanceClient {
  id: number;
  tradeName?: string;
  companyName?: string;
  status?: string;
}

interface AwsInstance {
  id: number;
  instanceId: string;
  instanceType: string;
  platform?: string;
  region: string;
  availabilityZone?: string;
  state: string;
  name?: string;
  launchTime?: string;
  privateIp?: string;
  publicIp?: string;
  reservationId?: string;
  onDemandOnly?: boolean;
  clientId?: number;
  tags?: Record<string, string>;
  lastSyncAt?: string;
  clients?: InstanceClient[];
}

interface DashboardData {
  totalActiveReservations: number;
  totalActiveUnits?: number;
  totalExpiredReservations: number;
  expiringIn30Days: number;
  expiringUnitsIn30Days?: number;
  idleReservations?: number;
  idleUnits?: number;
  idleMonthlyCost?: number;
  totalMonthlyCost?: number;
  totalInstances: number;
  runningInstances?: number;
  stoppedInstances?: number;
  coveredInstances: number;
  uncoveredInstances: number;
  /** @deprecated apelido antigo de totalMonthlyCost (o card mostra custo, nao economia) */
  estimatedMonthlySavings: number;
  nextExpiration: AwsReservation | null;
  reservationsByType: Record<string, number>;
  reservationsByRegion: Record<string, number>;
}

export default function AwsReservationsTab() {
  const confirmar = useConfirm();
  // D6 (11/10/2026): os avisos e as perguntas nativas do navegador viram o aviso e o confirm do HUB.
  const avisar = useAviso();
  const falhou = (e: any) => e?.response?.data?.message || mensagemDoErroDeCarga(e);
  // D5: a carga calava; na falha a tela dizia "Nenhuma reserva encontrada", sem cards e sem a faixa de expiração.
  const carga = useEstadoDaCarga();
  const rodarCarga = carga.rodar;
  const leu = carga.atualizadoEm !== null;
  const [reservations, setReservations] = useState<AwsReservation[]>([]);
  const [instances, setInstances] = useState<AwsInstance[]>([]);
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<ReservationFilter>('active');
  const [activeTab, setActiveTab] = useState<'reservations' | 'instances'>('reservations');
  // Filtro de cobertura da aba Instâncias (cards e chips apontam pra cá)
  const [instanceFilter, setInstanceFilter] = useState<'all' | 'covered' | 'uncovered' | 'stopped' | 'ondemand'>('all');
  const [instSort, toggleInstSort] = useSortState({ col: 'name', dir: 'asc' });
  const [modal, setModal] = useState<'create' | 'edit' | 'link' | 'manage-clients' | null>(null);
  const [allClients, setAllClients] = useState<any[]>([]);
  // D5: a lista de clientes do modal que não carrega diz, em vez de oferecer uma lista vazia.
  const [clientesErro, setClientesErro] = useState<string | null>(null);
  const [selectedReservation, setSelectedReservation] = useState<AwsReservation | null>(null);
  const [selectedInstance, setSelectedInstance] = useState<AwsInstance | null>(null);
  const [saving, setSaving] = useState(false);
  const [clientSearch, setClientSearch] = useState('');

  const [form, setForm] = useState<any>({
    instanceType: '',
    platform: 'Linux/UNIX',
    region: 'sa-east-1',
    instanceCount: 1,
    startDate: getTodayStr(),
    endDate: '',
    offeringType: 'No Upfront',
    term: 12,
    fixedPrice: 0,
    usagePrice: 0,
    monthlyRecurring: 0,
    description: '',
  });

  const loadData = useCallback(async () => {
    setLoading(true);
    // A lista de clientes (clientsService.getAll) só servia ao modal morto de "vincular cliente" (D6): saiu da carga.
    await rodarCarga(async () => {
      const [res, inst, dash] = await Promise.all([
        awsReservationsService.getAll(),
        awsInstancesService.getAll(),
        awsReservationsService.getDashboard(),
      ]);
      setReservations(Array.isArray(res) ? res : []);
      setInstances(Array.isArray(inst) ? inst : []);
      setDashboard(dash);
    });
    setLoading(false);
  }, [rodarCarga]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Os modais mostram a reserva/instância da carga: depois de vincular ou associar, a lista do modal era a de antes.
  useEffect(() => {
    setSelectedReservation((sel) => (sel ? reservations.find((r) => r.id === sel.id) || sel : sel));
    setSelectedInstance((sel) => (sel ? instances.find((i) => i.id === sel.id) || sel : sel));
  }, [reservations, instances]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      await awsReservationsService.sync();
      await loadData();
      avisar('Sincronização concluída.', 'success');
    } catch (e: any) {
      avisar('Erro ao sincronizar: ' + (e.response?.data?.message || e.message), 'error');
    }
    setSyncing(false);
  };

  const getDaysRemaining = (endDate: string) => {
    const end = new Date(endDate);
    const now = new Date();
    const diff = Math.ceil((end.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    return diff;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      // Cor (11/10/2026, PR-2 da medição): estado no mapa de tons. 23/10/2026 (padrão da Central): Ativa é o normal, neutra.
      case 'active': return TONE_BADGE.neutral;
      case 'expired': return TONE_BADGE.danger;
      default: return TONE_BADGE.neutral;
    }
  };

  const getInstanceStateColor = (state: string) => {
    switch (state) {
      case 'running': return TONE_BADGE.neutral; // o normal, neutro (23/10/2026)
      case 'stopped': return TONE_BADGE.warning;
      case 'terminated': return TONE_BADGE.danger;
      default: return TONE_BADGE.neutral;
    }
  };

  const getDaysRemainingColor = (days: number) => {
    if (days <= 30) return TONE_TEXT.danger;
    if (days <= 60) return TONE_TEXT.warning;
    // Prazo folgado é o normal: neutro (23/10/2026). O verde dizia "tudo bem" em quase toda linha e virava fundo.
    return 'text-central-texto';
  };

  const filteredReservations = reservations.filter((r) => {
    const matchesSearch =
      r.instanceType.toLowerCase().includes(search.toLowerCase()) ||
      r.region.toLowerCase().includes(search.toLowerCase()) ||
      r.reservationId?.toLowerCase().includes(search.toLowerCase());
    const matchesStatus =
      statusFilter === 'all'
        ? true
        : statusFilter === 'idle'
        ? r.status === 'active' && idleUnitsOf(r) > 0
        : statusFilter === 'expiring'
        ? r.status === 'active' &&
          getDaysRemaining(r.endDate) > 0 &&
          getDaysRemaining(r.endDate) <= 30
        : r.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const matchesInstanceFilter = (i: AwsInstance): boolean => {
    switch (instanceFilter) {
      case 'covered':
        return i.state === 'running' && !i.onDemandOnly && !!i.reservationId;
      case 'uncovered':
        return i.state === 'running' && !i.onDemandOnly && !i.reservationId;
      case 'stopped':
        return i.state !== 'running';
      case 'ondemand':
        return !!i.onDemandOnly;
      default:
        return true;
    }
  };

  // Chave de ordenação da coluna Reserva: sem cobertura primeiro (é o que
  // interessa na renovação), depois sob demanda, parada, vinculada
  const reservaRank = (i: AwsInstance): number => {
    if (i.state === 'running' && !i.onDemandOnly && !i.reservationId) return 0;
    if (i.onDemandOnly) return 1;
    if (i.state !== 'running') return 2;
    return 3;
  };

  const filteredInstances = instances
    .filter((i) => {
      const matchesSearch =
        i.instanceId.toLowerCase().includes(search.toLowerCase()) ||
        i.name?.toLowerCase().includes(search.toLowerCase()) ||
        i.instanceType.toLowerCase().includes(search.toLowerCase()) ||
        i.region.toLowerCase().includes(search.toLowerCase());
      return matchesSearch && matchesInstanceFilter(i);
    })
    .sort((a, b) => {
      const dir = instSort.dir === 'asc' ? 1 : -1;
      const val = (i: AwsInstance): string | number => {
        switch (instSort.col) {
          case 'id': return i.instanceId;
          case 'type': return i.instanceType;
          case 'region': return i.availabilityZone || i.region;
          case 'state': return i.state;
          case 'reserva': return reservaRank(i);
          default: return (i.name || '').toLowerCase();
        }
      };
      const va = val(a);
      const vb = val(b);
      if (va < vb) return -dir;
      if (va > vb) return dir;
      return 0;
    });

  const openCreate = () => {
    setForm({
      instanceType: '',
      platform: 'Linux/UNIX',
      region: 'sa-east-1',
      instanceCount: 1,
      startDate: getTodayStr(),
      endDate: '',
      offeringType: 'No Upfront',
      term: 12,
      fixedPrice: 0,
      usagePrice: 0,
      monthlyRecurring: 0,
      description: '',
    });
    setSelectedReservation(null);
    setModal('create');
  };

  const openEdit = (reservation: AwsReservation) => {
    setForm({
      instanceType: reservation.instanceType,
      platform: reservation.platform,
      region: reservation.region,
      instanceCount: reservation.instanceCount,
      startDate: reservation.startDate.split('T')[0],
      endDate: reservation.endDate.split('T')[0],
      offeringType: reservation.offeringType,
      term: reservation.term,
      fixedPrice: reservation.fixedPrice,
      usagePrice: reservation.usagePrice,
      monthlyRecurring: reservation.monthlyRecurring,
      description: reservation.description || '',
    });
    setSelectedReservation(reservation);
    setModal('edit');
  };

  const openLinkModal = (reservation: AwsReservation) => {
    setSelectedReservation(reservation);
    setModal('link');
  };

  // Flag manual "sob demanda": exclui da cobertura e do auto-link de reservas
  // mesmo com a instância executando (decisão de não renovar a reserva dela)
  const toggleOnDemand = async (instance: AwsInstance) => {
    const turningOn = !instance.onDemandOnly;
    if (!(await confirmar(confirmacaoDeSobDemanda(instance, turningOn)))) return;
    try {
      await awsInstancesService.update(instance.id, { onDemandOnly: turningOn });
      await loadData();
    } catch (e: any) {
      avisar(e.response?.data?.message || 'Erro ao atualizar instância', 'error');
    }
  };

  const openManageClientsModal = async (instance: AwsInstance) => {
    setSelectedInstance(instance);
    setClientSearch('');
    setModal('manage-clients');
    await recarregarClientes();
  };

  const recarregarClientes = async () => {
    try {
      const lidos = await awsInstancesService.getAllClients();
      setAllClients(Array.isArray(lidos) ? lidos : []);
      setClientesErro(null);
    } catch (e: any) {
      setAllClients([]);
      setClientesErro(mensagemDoErroDeCarga(e));
    }
  };

  // D1 (11/10/2026): associar MOVE o cliente (ele tem uma instância só) e troca a máquina do relatório técnico da
  // fatura. Era um clique, sem pergunta; o "(já em: …)" da linha era o único aviso.
  const handleAssociateClient = async (client: any) => {
    if (!selectedInstance) return;
    const atual = client.ec2InstanceId ? instances.find((i) => i.instanceId === client.ec2InstanceId) : undefined;
    if (!(await confirmar(confirmacaoDeAssociar(client, selectedInstance, atual)))) return;
    try {
      await awsInstancesService.associateClient(selectedInstance.instanceId, client.id);
    } catch (e: any) {
      avisar(`Não associei: ${falhou(e)}`, 'error');
      return;
    }
    await loadData();
    await recarregarClientes();
  };

  const handleDisassociateClient = async (client: InstanceClient) => {
    if (!selectedInstance) return;
    if (!(await confirmar(confirmacaoDeRemoverCliente(client, selectedInstance)))) return;
    try {
      await awsInstancesService.disassociateClient(client.id);
    } catch (e: any) {
      avisar(`Não removi: ${falhou(e)}`, 'error');
      return;
    }
    await loadData();
    await recarregarClientes();
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (modal === 'create') {
        await awsReservationsService.create(form);
      } else if (modal === 'edit' && selectedReservation) {
        await awsReservationsService.update(selectedReservation.id, form);
      }
      await loadData();
      setModal(null);
    } catch (e: any) {
      avisar('Erro ao salvar: ' + (e.response?.data?.message || e.message), 'error');
    }
    setSaving(false);
  };

  const handleDelete = async (r: AwsReservation) => {
    if (!(await confirmar(confirmacaoDeExcluirReserva(r)))) return;
    try {
      await awsReservationsService.delete(r.id);
      await loadData();
    } catch (e: any) {
      avisar('Erro ao excluir: ' + (e.response?.data?.message || e.message), 'error');
    }
  };

  const handleDismissAlert = async (r: AwsReservation) => {
    // D4: silenciar pergunta (reativar não precisa).
    if (!r.alertDismissed && !(await confirmar(confirmacaoDeSilenciar(r)))) return;
    try {
      await awsReservationsService.dismissAlert(r.id);
      await loadData();
    } catch (e: any) {
      avisar('Erro ao alterar alerta: ' + (e.response?.data?.message || e.message), 'error');
    }
  };

  const handleLinkInstance = async (instanceId: string) => {
    if (!selectedReservation) return;
    try {
      await awsReservationsService.linkInstance(selectedReservation.id, instanceId);
      await loadData();
      avisar('Instância vinculada.', 'success');
    } catch (e: any) {
      avisar('Erro ao vincular: ' + (e.response?.data?.message || e.message), 'error');
    }
  };

  const handleUnlinkInstance = async (reservationId: number, instanceId: string) => {
    const instancia = instances.find((i) => i.instanceId === instanceId) || { instanceId };
    if (selectedReservation && !(await confirmar(confirmacaoDeDesvincular(instancia, selectedReservation)))) return;
    try {
      await awsReservationsService.unlinkInstance(reservationId, instanceId);
      await loadData();
    } catch (e: any) {
      avisar('Erro ao desvincular: ' + (e.response?.data?.message || e.message), 'error');
    }
  };

  const availableInstances = instances.filter(
    (i) =>
      !i.reservationId &&
      i.state === 'running' &&
      (!selectedReservation ||
        (i.instanceType === selectedReservation.instanceType &&
          i.region === selectedReservation.region))
  );

  // Expiring reservations alert
  const expiringReservations = reservations.filter(
    (r) => r.status === 'active' && getDaysRemaining(r.endDate) <= 30 && getDaysRemaining(r.endDate) > 0
  );

  // Agregados calculados aqui, e nao lidos do /dashboard, para a tela nao
  // depender da ordem de deploy (Vercel publica sozinha, o backend e manual).
  const activeReservations = reservations.filter((r) => r.status === 'active');
  const sumUnits = (list: AwsReservation[]) =>
    list.reduce((sum, r) => sum + (r.instanceCount || 0), 0);
  const activeUnits = sumUnits(activeReservations);
  const expiringUnits = sumUnits(expiringReservations);
  const idleReservations = activeReservations.filter((r) => idleUnitsOf(r) > 0);
  const idleUnits = idleReservations.reduce((sum, r) => sum + idleUnitsOf(r), 0);
  const idleMonthlyCost = idleReservations.reduce((sum, r) => sum + idleCostOf(r), 0);
  const totalMonthlyCost = activeReservations.reduce(
    (sum, r) => sum + (parseFloat(String(r.monthlyRecurring)) || 0),
    0,
  );

  if (loading && !leu && carga.estado !== 'erro') {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Expiring Alert. D5: sem a carga, a faixa diz que não verificou (sumir parecia "nada vencendo"). */}
      {!leu && carga.estado === 'erro' && (
        <p className={`text-sm ${TONE_TEXT.warning}`} data-testid="expiracao-nao-verificada">
          Reservas expirando: não verificado, a lista não carregou.
        </p>
      )}
      {leu && expiringReservations.length > 0 && (
        <div className={`bg-central-cartao border ${TONE_BORDER.warning} rounded-central p-4`}>
          <div className="flex items-center gap-3">
            <Icon name="alertTriangle" className={`w-6 h-6 ${TONE_TEXT.warning}`} />
            <div>
              <h3 className={`font-semibold ${TONE_TEXT.warning}`}>Reservas Expirando</h3>
              <p className="text-sm text-slate-400">
                {expiringUnits} unidade(s) em {expiringReservations.length} reserva(s) expirando nos próximos 30 dias
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Os cartões do topo (padrão das listas, 23/10/2026): o CartaoDoTopo, em duas linhas de quatro (em oito colunas o "US$
          1.122,42" saía do cartão). Valor branco; o escolhido pela barra primary; a barra no tom só no que pede ação: expirando,
          sem cobertura e reserva ociosa, quando há. */}
      {(dashboard || !leu) && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <CartaoDoTopo
            rotulo="Unidades Ativas" valor={leu ? activeUnits : textos.numeroSemFonte} sub={`${activeReservations.length} reservas`}
            ativo={activeTab === 'reservations' && statusFilter === 'active'} onClick={() => { setStatusFilter('active'); setActiveTab('reservations'); }}
          />
          <CartaoDoTopo
            rotulo="Expirando 30d" valor={leu ? expiringUnits : textos.numeroSemFonte} sub={`${expiringReservations.length} reservas`}
            barra={leu && expiringUnits > 0 ? 'warning' : null}
            ativo={activeTab === 'reservations' && statusFilter === 'expiring'} onClick={() => { setStatusFilter('expiring'); setActiveTab('reservations'); }}
          />
          <CartaoDoTopo
            rotulo="Expiradas" valor={dashboard ? dashboard.totalExpiredReservations : textos.numeroSemFonte}
            ativo={activeTab === 'reservations' && statusFilter === 'expired'} onClick={() => { setStatusFilter('expired'); setActiveTab('reservations'); }}
          />
          <CartaoDoTopo
            rotulo="Total Instâncias" valor={dashboard ? dashboard.totalInstances : textos.numeroSemFonte}
            sub={(dashboard?.stoppedInstances || 0) > 0
              ? <span className={TONE_TEXT.warning}>{dashboard!.stoppedInstances} parada{dashboard!.stoppedInstances! > 1 ? 's' : ''}</span>
              : undefined}
            ativo={activeTab === 'instances' && instanceFilter === 'all'} onClick={() => { setActiveTab('instances'); setInstanceFilter('all'); }}
          />
          <CartaoDoTopo
            rotulo="Cobertas" valor={dashboard ? dashboard.coveredInstances : textos.numeroSemFonte}
            ativo={activeTab === 'instances' && instanceFilter === 'covered'} onClick={() => { setActiveTab('instances'); setInstanceFilter('covered'); }}
          />
          <CartaoDoTopo
            rotulo="Sem Cobertura" valor={dashboard ? dashboard.uncoveredInstances : textos.numeroSemFonte} sub="só executando"
            barra={(dashboard?.uncoveredInstances || 0) > 0 ? 'danger' : null}
            ativo={activeTab === 'instances' && instanceFilter === 'uncovered'} onClick={() => { setActiveTab('instances'); setInstanceFilter('uncovered'); }}
          />
          <CartaoDoTopo
            rotulo="Reserva Ociosa" valor={leu ? `US$ ${fmt(idleMonthlyCost)}` : textos.numeroSemFonte} sub={`${idleUnits} vaga${idleUnits === 1 ? '' : 's'}`}
            barra={leu && idleUnits > 0 ? 'warning' : null}
            ativo={activeTab === 'reservations' && statusFilter === 'idle'} onClick={() => { setStatusFilter('idle'); setActiveTab('reservations'); }}
          />
          <CartaoDoTopo rotulo="Custo Mensal (USD)" valor={leu ? `US$ ${fmt(totalMonthlyCost)}` : textos.numeroSemFonte} />
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 border-b border-central-borda" role="tablist">
        <button
          onClick={() => setActiveTab('reservations')}
          role="tab"
          aria-selected={activeTab === 'reservations'}
          className={`min-h-[44px] px-4 text-[15px] border-b-[3px] -mb-px transition-colors ${
            activeTab === 'reservations'
              ? 'border-central-texto text-central-texto font-semibold'
              : 'border-transparent text-central-texto-fraco font-medium hover:text-central-texto'
          }`}
        >
          Reservas ({filteredReservations.length})
        </button>
        <button
          onClick={() => setActiveTab('instances')}
          role="tab"
          aria-selected={activeTab === 'instances'}
          className={`min-h-[44px] px-4 text-[15px] border-b-[3px] -mb-px transition-colors ${
            activeTab === 'instances'
              ? 'border-central-texto text-central-texto font-semibold'
              : 'border-transparent text-central-texto-fraco font-medium hover:text-central-texto'
          }`}
        >
          Instâncias ({instances.length})
        </button>
      </div>

      {/* Actions Bar */}
      <div className="flex flex-wrap gap-4 items-center justify-between">
        <div className="flex gap-3 items-center">
          <div className="relative">
            <Icon name="search" className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Buscar..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className={`pl-10 pr-4 py-2 rounded-lg placeholder-slate-400 w-64 ${campo}`}
            />
          </div>

        </div>

        <div className="flex gap-3">
          <button
            onClick={handleSync}
            disabled={syncing}
            className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors disabled:opacity-50"
          >
            <Icon name="refresh" className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Sincronizando...' : 'Sincronizar AWS'}
          </button>

          {activeTab === 'reservations' && carga.estado === 'pronto' && (
            <button
              onClick={openCreate}
              className="flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary-strong text-white rounded-lg transition-colors"
            >
              <Icon name="plus" className="w-4 h-4" />
              Nova Reserva
            </button>
          )}
        </div>
      </div>

      <EstadoDaLista carga={carga} temDados={leu} oQue="as reservas e as instâncias" onTentarDeNovo={loadData} vazio={A_PROPRIA_LISTA_DIZ_O_VAZIO}>
      {/* Reservations Table */}
      {activeTab === 'reservations' && (
        <CascaDaLista>
          <div className="flex flex-wrap gap-2 p-3 border-b border-central-borda">
            {([
              ['active', 'Ativas'],
              ['idle', 'Ociosas'],
              ['expiring', 'Expirando 30d'],
              ['expired', 'Expiradas'],
              ['cancelled', 'Canceladas'],
              ['all', 'Todas'],
            ] as const).map(([key, label]) => {
              const count =
                key === 'all'
                  ? reservations.length
                  : key === 'idle'
                  ? idleReservations.length
                  : key === 'expiring'
                  ? expiringReservations.length
                  : reservations.filter((r) => r.status === key).length;
              return (
                <button
                  key={key}
                  onClick={() => setStatusFilter(key)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    statusFilter === key
                      ? 'bg-slate-600 text-white font-semibold border border-slate-500'
                      : 'bg-slate-700/50 text-slate-300 border border-transparent hover:bg-slate-700'
                  }`}
                >
                  {label} ({count})
                </button>
              );
            })}
          </div>
          {statusFilter === 'idle' && (
            <div className={`px-4 py-3 border-b border-central-borda text-sm ${TONE_TEXT.warning}`}>
              Vaga de reserva sem instância ocupando. Custa igual, cobrindo ou não.
              Antes de renovar, decida: religar/subir uma máquina do tipo, ou deixar vencer.
              {idleUnits > 0 && (
                <> Total hoje: <strong>{idleUnits} vaga{idleUnits === 1 ? '' : 's'}, US$ {fmt(idleMonthlyCost)}/mês</strong>.</>
              )}
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full">
              <CabecalhoDaLista>
                <ColunaDaLista rotulo="Tipo" />
                <ColunaDaLista rotulo="Plataforma" />
                <ColunaDaLista rotulo="Região" />
                <ColunaDaLista rotulo="Qtd" />
                <ColunaDaLista rotulo="Início" />
                <ColunaDaLista rotulo="Término" />
                <ColunaDaLista rotulo="Dias Rest." />
                <ColunaDaLista rotulo="Status" />
                <ColunaDaLista rotulo="Vinculadas" />
                <ColunaDaLista rotulo="Custo/mês" alinhamento="direita" />
                <ColunaDaLista rotulo="Ações" alinhamento="direita" />
              </CabecalhoDaLista>
              <tbody>
                {filteredReservations.map((r) => {
                  const daysRemaining = getDaysRemaining(r.endDate);
                  const idle = r.status === 'active' ? idleUnitsOf(r) : 0;
                  return (
                    <LinhaDaLista key={r.id} data-ociosa={idle > 0 ? '' : undefined}>
                      <td className={`${CELULA} ${barraDoEstado(idle > 0 ? 'warning' : null)}`}>
                        <span className="font-mono text-sm text-central-texto">{r.instanceType}</span>
                      </td>
                      <td className="px-4 py-3 text-slate-300">{r.platform}</td>
                      <td className="px-4 py-3 text-slate-300">{r.region}</td>
                      <td className="px-4 py-3 text-slate-300">{r.instanceCount}</td>
                      <td className="px-4 py-3 text-slate-300">{fmtDate(r.startDate)}</td>
                      <td className="px-4 py-3 text-slate-300">{fmtDate(r.endDate)}</td>
                      <td className={`px-4 py-3 font-semibold ${getDaysRemainingColor(daysRemaining)}`}>
                        {daysRemaining > 0 ? daysRemaining : 'Expirada'}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(r.status)}`}>
                          {r.status === 'active' ? 'Ativa' : r.status === 'expired' ? 'Expirada' : 'Cancelada'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {(() => {
                          const linked = r.linkedInstances || [];
                          const stoppedLinked = linked.filter(id => {
                            const inst = instances.find(i => i.instanceId === id);
                            return inst && inst.state !== 'running';
                          }).length;
                          return (
                            <div className="flex flex-col">
                              <span
                                className={idle > 0 ? `${TONE_TEXT.warning} font-semibold` : stoppedLinked > 0 ? TONE_TEXT.warning : 'text-slate-300'}
                                title={stoppedLinked > 0 ? 'Instância vinculada parada — ao renovar, considere reduzir a quantidade' : undefined}
                              >
                                {linked.length}/{r.instanceCount}
                                {stoppedLinked > 0 && <> ⚠ {stoppedLinked} parada{stoppedLinked > 1 ? 's' : ''}</>}
                              </span>
                              {idle > 0 && (
                                <span className={`text-xs ${TONE_TEXT.warning}`}>
                                  {idle} ociosa{idle > 1 ? 's' : ''} · US$ {fmt(idleCostOf(r))}/mês
                                </span>
                              )}
                            </div>
                          );
                        })()}
                      </td>
                      <td className="px-4 py-3 text-right text-slate-300 tabular-nums whitespace-nowrap">
                        US$ {fmt(r.monthlyRecurring)}
                      </td>
                      <CelulaAcoes
                        rotuloDoMenu={`Mais ações da reserva ${r.instanceType} (${fmtDate(r.endDate)})`}
                        onEditar={() => openEdit(r)}
                        Icon={Icon}
                        antes={<>
                          <button onClick={() => openLinkModal(r)} className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg" title="Vincular Instâncias">
                            <Icon name="link" className="w-4 h-4" />
                          </button>
                          {/* O sino fica à vista: ele mostra o estado (silenciado em âmbar). */}
                          <button
                            onClick={() => handleDismissAlert(r)}
                            className={`p-1.5 rounded-lg hover:bg-slate-700 ${r.alertDismissed ? `${TONE_TEXT.warning} hover:text-white` : 'text-slate-400 hover:text-white'}`}
                            title={r.alertDismissed ? 'Reativar alerta' : 'Silenciar alerta'}
                          >
                            <Icon name={r.alertDismissed ? 'bellOff' : 'bell'} className="w-4 h-4" />
                          </button>
                        </>}
                        itens={[{ label: 'Excluir', onClick: () => handleDelete(r), danger: true }]}
                      />
                    </LinhaDaLista>
                  );
                })}
                {filteredReservations.length === 0 && (
                  <tr>
                    <td colSpan={11} className="px-4 py-8 text-center text-slate-400">
                      {statusFilter === 'idle'
                        ? 'Nenhuma reserva ociosa. Toda vaga ativa está ocupada por uma instância.'
                        : 'Nenhuma reserva encontrada'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CascaDaLista>
      )}

      {/* Instances Table */}
      {activeTab === 'instances' && (
        <CascaDaLista>
          <div className="flex flex-wrap gap-2 p-3 border-b border-central-borda">
            {([
              ['all', 'Todas'],
              ['uncovered', 'Sem cobertura'],
              ['covered', 'Cobertas'],
              ['stopped', 'Paradas'],
              ['ondemand', 'Sob demanda'],
            ] as const).map(([key, label]) => {
              const count = key === 'all' ? instances.length : instances.filter(i => {
                switch (key) {
                  case 'covered': return i.state === 'running' && !i.onDemandOnly && !!i.reservationId;
                  case 'uncovered': return i.state === 'running' && !i.onDemandOnly && !i.reservationId;
                  case 'stopped': return i.state !== 'running';
                  default: return !!i.onDemandOnly;
                }
              }).length;
              return (
                <button
                  key={key}
                  onClick={() => setInstanceFilter(key)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    instanceFilter === key
                      ? 'bg-slate-600 text-white font-semibold border border-slate-500'
                      : 'bg-slate-700/50 text-slate-300 border border-transparent hover:bg-slate-700'
                  }`}
                >
                  {label} ({count})
                </button>
              );
            })}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <CabecalhoDaLista>
                  <SortHeader label="ID" col="id" sort={instSort} onToggle={toggleInstSort} className="px-4 py-3 text-left text-xs font-semibold uppercase" />
                  <SortHeader label="Nome" col="name" sort={instSort} onToggle={toggleInstSort} className="px-4 py-3 text-left text-xs font-semibold uppercase" />
                  <SortHeader label="Tipo" col="type" sort={instSort} onToggle={toggleInstSort} className="px-4 py-3 text-left text-xs font-semibold uppercase" />
                  <SortHeader label="Região" col="region" sort={instSort} onToggle={toggleInstSort} className="px-4 py-3 text-left text-xs font-semibold uppercase" />
                  <SortHeader label="Estado" col="state" sort={instSort} onToggle={toggleInstSort} className="px-4 py-3 text-left text-xs font-semibold uppercase" />
                  <ColunaDaLista rotulo="IP Privado" />
                  <ColunaDaLista rotulo="IP Público" />
                  <ColunaDaLista rotulo="Clientes" />
                  <SortHeader label="Reserva" col="reserva" sort={instSort} onToggle={toggleInstSort} className="px-4 py-3 text-left text-xs font-semibold uppercase" />
                  <ColunaDaLista rotulo="Ações" />
              </CabecalhoDaLista>
              <tbody>
                {filteredInstances.map((i) => (
                  <LinhaDaLista key={i.id}>
                    <td className="px-4 py-3">
                      <span className="font-mono text-xs text-slate-300">{i.instanceId}</span>
                    </td>
                    <td className="px-4 py-3 text-central-texto">{i.name || '-'}</td>
                    <td className="px-4 py-3">
                      <span className="font-mono text-sm text-slate-300">{i.instanceType}</span>
                    </td>
                    <td className="px-4 py-3 text-slate-300">{i.availabilityZone || i.region}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getInstanceStateColor(i.state)}`}>
                        {i.state}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-mono text-xs text-slate-300">{i.privateIp || '-'}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-mono text-xs text-slate-300">{i.publicIp || '-'}</span>
                    </td>
                    <td className="px-4 py-3">
                      {i.clients && i.clients.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {i.clients.slice(0, 3).map((c) => (
                            <span
                              key={c.id}
                              className="px-2 py-0.5 rounded text-xs bg-slate-700 text-slate-200"
                              title={c.companyName}
                            >
                              {nomeDeExibicao(c)}
                            </span>
                          ))}
                          {i.clients.length > 3 && (
                            <span className="px-2 py-0.5 rounded text-xs bg-slate-600 text-slate-300">
                              +{i.clients.length - 3}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-slate-500 text-sm">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {i.onDemandOnly ? (
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${TONE_BADGE.info}`}>
                          Sob demanda
                        </span>
                      ) : i.reservationId ? (
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${TONE_BADGE.neutral}`}>
                          Vinculada
                        </span>
                      ) : (
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${TONE_BADGE.neutral}`}>
                          Sem reserva
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <button
                          onClick={() => openManageClientsModal(i)}
                          className="p-1.5 text-slate-400 hover:text-white transition-colors"
                          title="Gerenciar Clientes"
                        >
                          <Icon name="user" className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => toggleOnDemand(i)}
                          className={`p-1.5 transition-colors ${i.onDemandOnly ? `${TONE_TEXT.info} hover:text-white` : 'text-slate-400 hover:text-white'}`}
                          title={i.onDemandOnly ? 'Marcada SOB DEMANDA (fora da cobertura/reserva) — clique para voltar a contar' : 'Marcar como sob demanda: sai da cobertura e não recebe vaga de reserva'}
                        >
                          <Icon name="dollar" className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </LinhaDaLista>
                ))}
                {filteredInstances.length === 0 && (
                  <tr>
                    <td colSpan={10} className="px-4 py-8 text-center text-slate-400">
                      Nenhuma instância encontrada
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CascaDaLista>
      )}

      </EstadoDaLista>

      {/* Create/Edit Modal */}
      {(modal === 'create' || modal === 'edit') && (() => { const daAws = modal === 'edit' && !!selectedReservation?.reservationId; return (
        // Formulário: não fecha ao clicar fora, como já era.
        <CascaDeModal
          largura="base"
          titulo={modal === 'create' ? 'Nova Reserva' : 'Editar Reserva'}
          onClose={() => setModal(null)}
          corpoClassName="p-4 space-y-4"
          rodape={
            <div className="ml-auto flex gap-3">
              <button
                onClick={() => setModal(null)}
                className="px-4 py-2 text-slate-300 hover:text-white transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !form.instanceType || !form.startDate || !form.endDate}
                className="px-4 py-2 bg-primary hover:bg-primary-strong text-white rounded-lg transition-colors disabled:opacity-50"
              >
                {saving ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          }
        >
              {daAws && <p className="text-xs text-slate-400" data-testid="frase-da-aws">{FRASE_DA_RESERVA_DA_AWS}</p>}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">Tipo de Instância *</label>
                  <input
                    type="text"
                    value={form.instanceType}
                    onChange={(e) => setForm({ ...form, instanceType: e.target.value })}
                    placeholder="ex: t3.medium"
                    disabled={daAws}
                    className={`w-full px-3 py-2 ${daAws ? campoSomenteLeitura : campo} rounded-lg`}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">Plataforma *</label>
                  <select
                    value={form.platform}
                    onChange={(e) => setForm({ ...form, platform: e.target.value })}
                    disabled={daAws}
                    className={`w-full px-3 py-2 ${daAws ? campoSomenteLeitura : campo} rounded-lg`}
                  >
                    <option value="Linux/UNIX">Linux/UNIX</option>
                    <option value="Windows">Windows</option>
                    <option value="Red Hat Enterprise Linux">Red Hat Enterprise Linux</option>
                    <option value="SUSE Linux">SUSE Linux</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">Região *</label>
                  <select
                    value={form.region}
                    onChange={(e) => setForm({ ...form, region: e.target.value })}
                    disabled={daAws}
                    className={`w-full px-3 py-2 ${daAws ? campoSomenteLeitura : campo} rounded-lg`}
                  >
                    <option value="sa-east-1">sa-east-1 (São Paulo)</option>
                    <option value="us-east-1">us-east-1 (N. Virginia)</option>
                    <option value="us-east-2">us-east-2 (Ohio)</option>
                    <option value="us-west-1">us-west-1 (N. California)</option>
                    <option value="us-west-2">us-west-2 (Oregon)</option>
                    <option value="eu-west-1">eu-west-1 (Ireland)</option>
                    <option value="eu-central-1">eu-central-1 (Frankfurt)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">Quantidade *</label>
                  <input
                    type="number"
                    min="1"
                    value={form.instanceCount}
                    onChange={(e) => setForm({ ...form, instanceCount: parseInt(e.target.value) || 1 })}
                    disabled={daAws}
                    className={`w-full px-3 py-2 ${daAws ? campoSomenteLeitura : campo} rounded-lg`}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">Data Início *</label>
                  <input
                    type="date"
                    value={form.startDate}
                    onChange={(e) => setForm({ ...form, startDate: e.target.value })}
                    disabled={daAws}
                    className={`w-full px-3 py-2 ${daAws ? campoSomenteLeitura : campo} rounded-lg`}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">Data Término *</label>
                  <input
                    type="date"
                    value={form.endDate}
                    onChange={(e) => setForm({ ...form, endDate: e.target.value })}
                    disabled={daAws}
                    className={`w-full px-3 py-2 ${daAws ? campoSomenteLeitura : campo} rounded-lg`}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">Tipo de Oferta</label>
                  <select
                    value={form.offeringType}
                    onChange={(e) => setForm({ ...form, offeringType: e.target.value })}
                    disabled={daAws}
                    className={`w-full px-3 py-2 ${daAws ? campoSomenteLeitura : campo} rounded-lg`}
                  >
                    <option value="No Upfront">No Upfront</option>
                    <option value="Partial Upfront">Partial Upfront</option>
                    <option value="All Upfront">All Upfront</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">Termo (meses)</label>
                  <select
                    value={form.term}
                    onChange={(e) => setForm({ ...form, term: parseInt(e.target.value) })}
                    disabled={daAws}
                    className={`w-full px-3 py-2 ${daAws ? campoSomenteLeitura : campo} rounded-lg`}
                  >
                    <option value={12}>12 meses (1 ano)</option>
                    <option value={36}>36 meses (3 anos)</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">Preço Fixo (R$)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.fixedPrice}
                    onChange={(e) => setForm({ ...form, fixedPrice: parseFloat(e.target.value) || 0 })}
                    disabled={daAws}
                    className={`w-full px-3 py-2 ${daAws ? campoSomenteLeitura : campo} rounded-lg`}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">Preço/Hora (R$)</label>
                  <input
                    type="number"
                    step="0.0001"
                    min="0"
                    value={form.usagePrice}
                    onChange={(e) => setForm({ ...form, usagePrice: parseFloat(e.target.value) || 0 })}
                    disabled={daAws}
                    className={`w-full px-3 py-2 ${daAws ? campoSomenteLeitura : campo} rounded-lg`}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">Custo mensal total (US$)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.monthlyRecurring}
                    onChange={(e) => setForm({ ...form, monthlyRecurring: parseFloat(e.target.value) || 0 })}
                    disabled={daAws}
                    className={`w-full px-3 py-2 ${daAws ? campoSomenteLeitura : campo} rounded-lg`}
                  />
                  <p className="text-xs text-slate-500 mt-1">Da linha inteira, não de uma unidade. É o que a AWS cobra por todas as {form.instanceCount || 1} juntas.</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">Descrição/Notas</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={3}
                  className={`w-full px-3 py-2 ${campo} rounded-lg resize-none`}
                />
              </div>
        </CascaDeModal>
      ); })()}

      {/* Link Instance Modal */}
      {modal === 'link' && selectedReservation && (
        // Cada vínculo grava no clique; não fechava ao clicar fora e continua não
        // fechando (sem mudança de comportamento). A reserva vai para a linha mono.
        <CascaDeModal
          largura="sm"
          titulo="Vincular Instâncias"
          subtitulo={`${selectedReservation.instanceType} · ${selectedReservation.region}`}
          onClose={() => setModal(null)}
          corpoClassName="p-4 space-y-4"
          rodape={
            <button
              onClick={() => setModal(null)}
              className="ml-auto px-4 py-2 text-slate-300 hover:text-white transition-colors"
            >
              Fechar
            </button>
          }
        >
              <div className="bg-slate-700/50 rounded-lg p-3">
                <p className="text-sm text-slate-300">
                  Reserva: <span className="font-mono text-white">{selectedReservation.instanceType}</span> ({selectedReservation.region})
                </p>
                <p className="text-sm text-slate-300">
                  Vinculadas: {(selectedReservation.linkedInstances?.length || 0)}/{selectedReservation.instanceCount}
                </p>
              </div>

              {/* Currently linked */}
              {selectedReservation.linkedInstances && selectedReservation.linkedInstances.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-slate-300 mb-2">Instâncias Vinculadas</h3>
                  <div className="space-y-2">
                    {selectedReservation.linkedInstances.map((instanceId) => {
                      const instance = instances.find((i) => i.instanceId === instanceId);
                      return (
                        <div
                          key={instanceId}
                          className="flex items-center justify-between bg-slate-700/50 rounded-lg p-2"
                        >
                          <div>
                            <span className="font-mono text-sm text-white">{instanceId}</span>
                            {instance?.name && (
                              <span className="text-slate-400 ml-2">({instance.name})</span>
                            )}
                          </div>
                          <button
                            onClick={() => handleUnlinkInstance(selectedReservation.id, instanceId)}
                            className="text-red-400 hover:text-red-300"
                          >
                            <Icon name="unlink" className="w-4 h-4" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Available instances */}
              <div>
                <h3 className="text-sm font-medium text-slate-300 mb-2">Instâncias Disponíveis</h3>
                {availableInstances.length === 0 ? (
                  <p className="text-slate-400 text-sm">
                    Nenhuma instância compatível disponível ({selectedReservation.instanceType} em {selectedReservation.region})
                  </p>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {availableInstances.map((instance) => (
                      <div
                        key={instance.id}
                        className="flex items-center justify-between bg-slate-700/50 rounded-lg p-2 hover:bg-slate-700"
                      >
                        <div>
                          <span className="font-mono text-sm text-white">{instance.instanceId}</span>
                          {instance.name && (
                            <span className="text-slate-400 ml-2">({instance.name})</span>
                          )}
                        </div>
                        <button
                          onClick={() => handleLinkInstance(instance.instanceId)}
                          className="text-slate-300 hover:text-white"
                        >
                          <Icon name="link" className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
        </CascaDeModal>
      )}

      {/* Manage Clients Modal */}
      {modal === 'manage-clients' && selectedInstance && (
        // Cada associação grava no clique; não fechava ao clicar fora e continua não
        // fechando. A instância vai para a linha mono.
        <CascaDeModal
          largura="base"
          titulo="Gerenciar Clientes da Instância"
          subtitulo={selectedInstance.name ? `${selectedInstance.instanceId} · ${selectedInstance.name}` : selectedInstance.instanceId}
          onClose={() => setModal(null)}
          corpoClassName="p-4 space-y-4"
          rodape={
            <button
              onClick={() => setModal(null)}
              className="ml-auto px-4 py-2 text-slate-300 hover:text-white transition-colors"
            >
              Fechar
            </button>
          }
        >
              <div className="bg-slate-700/50 rounded-lg p-3">
                <p className="text-sm text-slate-300">
                  Instância: <span className="font-mono text-white">{selectedInstance.instanceId}</span>
                </p>
                {selectedInstance.name && (
                  <p className="text-sm text-slate-300">
                    Nome: <span className="text-white">{selectedInstance.name}</span>
                  </p>
                )}
              </div>

              {/* Associated Clients */}
              <div>
                <h3 className="text-sm font-medium text-slate-300 mb-2">
                  Clientes Associados ({selectedInstance.clients?.length || 0})
                </h3>
                {selectedInstance.clients && selectedInstance.clients.length > 0 ? (
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {selectedInstance.clients.map((client) => (
                      <div
                        key={client.id}
                        className="flex items-center justify-between bg-slate-700/50 border border-slate-600 rounded-lg p-2"
                      >
                        <span className="text-slate-200">{nomeDeExibicao(client)}</span>
                        <button
                          onClick={() => handleDisassociateClient(client)}
                          className="p-1 text-red-400 hover:text-red-300"
                          title="Remover associação"
                        >
                          <Icon name="x" className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-sm">Nenhum cliente associado</p>
                )}
              </div>

              {/* Available Clients */}
              <div>
                <h3 className="text-sm font-medium text-slate-300 mb-2">Adicionar Cliente</h3>
                <div className="relative mb-2">
                  <Icon name="search" className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Buscar cliente..."
                    value={clientSearch}
                    onChange={(e) => setClientSearch(e.target.value)}
                    className={`w-full pl-10 pr-4 py-2 rounded-lg placeholder-slate-400 ${campo}`}
                  />
                </div>
                {clientesErro && (
                  <div role="alert" className={`text-sm ${TONE_TEXT.danger} space-y-2`}>
                    <p>Não consegui carregar os clientes ({clientesErro}): não dá para associar daqui agora.</p>
                    <button onClick={recarregarClientes} className="px-3 py-1.5 bg-slate-700 text-white rounded-lg hover:bg-slate-600 text-xs">Tentar de novo</button>
                  </div>
                )}
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {allClients
                    .filter((c) => !c.ec2InstanceId || c.ec2InstanceId !== selectedInstance.instanceId)
                    .filter((c) => {
                      if (!clientSearch) return true;
                      const searchLower = clientSearch.toLowerCase();
                      return (
                        c.name?.toLowerCase().includes(searchLower) ||
                        c.tradeName?.toLowerCase().includes(searchLower) ||
                        c.companyName?.toLowerCase().includes(searchLower)
                      );
                    })
                    .map((client) => (
                      <button
                        key={client.id}
                        onClick={() => handleAssociateClient(client)}
                        className="w-full flex items-center justify-between bg-slate-700/50 rounded-lg p-2 hover:bg-slate-700 text-left"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-white font-medium truncate">
                              {client.name || client.tradeName || client.companyName}
                            </span>
                            {client.ec2InstanceId && (
                              <span className={`${TONE_TEXT.warning} text-xs whitespace-nowrap`}>
                                (já em: {client.ec2InstanceId.slice(-8)})
                              </span>
                            )}
                          </div>
                          {(client.name || client.tradeName) && client.companyName &&
                           (client.name || client.tradeName) !== client.companyName && (
                            <span className="text-slate-400 text-xs truncate block">
                              {client.companyName}
                            </span>
                          )}
                        </div>
                        <Icon name="plus" className="w-4 h-4 text-slate-300 flex-shrink-0 ml-2" />
                      </button>
                    ))}
                </div>
              </div>
        </CascaDeModal>
      )}
    </div>
  );
}
